import React, { useState, useEffect } from 'react';
import Button from 'components/base/Button';
import { Modal, Form, Row, Col } from 'react-bootstrap';
import DatePicker from 'components/base/DatePicker';
import dayjs from 'dayjs';
import { CarEvent, Car } from 'providers/CarManagmentProvider';

interface EditMaintenanceModalProps {
  show: boolean;
  car: Car | null;
  event: CarEvent | null;
  onHide: () => void;
  onEdit: (event: {
    id: number;
    idcarro: number;
    stateId: number;
    startDate: Date;
    endDate: Date | null;
    observation: string;
    local: string;
    isRead: number;
    isResolved: number;
  }) => Promise<void>;
  readOnly?: boolean;
}

const EditMaintenanceModal: React.FC<EditMaintenanceModalProps> = ({
  show,
  car,
  event,
  onHide,
  onEdit,
  readOnly = false
}) => {
  const [startDay, setStartDay] = useState<Date>(new Date());
  const [endDay, setEndDay] = useState<Date>(new Date());
  const [startTime, setStartTime] = useState<string>('09:00');
  const [endTime, setEndTime] = useState<string>('18:00');
  const [hasEndDate, setHasEndDate] = useState<boolean>(false);
  const [stateId, setStateId] = useState<number>(3);
  const [observation, setObservation] = useState<string>('');
  const [local, setLocal] = useState<string>('');

  // Initialize form with event data when modal opens
  useEffect(() => {
    if (event) {
      const startDateObj = event.startDate ? dayjs(event.startDate) : dayjs();
      const endDateObj = event.endDate ? dayjs(event.endDate) : dayjs();

      setStartDay(startDateObj.toDate());
      setEndDay(endDateObj.toDate());
      setStartTime(startDateObj.format('HH:mm'));
      setEndTime(endDateObj.format('HH:mm'));
      setHasEndDate(!!event.endDate);
      setStateId(event.stateId);
      setObservation(event.observation || '');
      setLocal(event.local || '');
    }
  }, [event, show]);

  const handleUpdate = async () => {
    if (!car || !event || stateId === undefined) {
      alert('Intervenção e data de início são obrigatórios.');
      return;
    }

    const [startHour, startMin] = startTime.split(':').map(Number);
    const startDateTime = new Date(startDay);
    startDateTime.setHours(startHour, startMin, 0, 0);

    let endDateTime = null;
    if (hasEndDate) {
      const [endHour, endMin] = endTime.split(':').map(Number);
      endDateTime = new Date(endDay);
      endDateTime.setHours(endHour, endMin, 0, 0);
    }

    await onEdit({
      id: event.id,
      idcarro: car.id,
      stateId,
      startDate: startDateTime,
      endDate: endDateTime,
      observation,
      local,
      isRead: event.isRead,
      isResolved: event.isResolved
    });
  };

  const handleHide = () => {
    setStartDay(new Date());
    setEndDay(new Date());
    setStartTime('09:00');
    setEndTime('18:00');
    setHasEndDate(false);
    setStateId(3);
    setObservation('');
    setLocal('');
    onHide();
  };

  const customStyles = `
    .end-date-container {
      overflow: hidden;
      max-height: 0;
      opacity: 0;
      transition: max-height 0.3s ease-out, opacity 0.2s ease-out;
    }

    .end-date-container.show {
      max-height: 200px;
      opacity: 1;
      margin-bottom: 1rem;
    }

    .form-label {
      margin-bottom: 0.5rem;
    }
  `;

  return (
    <Modal
      show={show}
      onHide={handleHide}
      contentClassName="border-translucent"
      centered
    >
      <style>{customStyles}</style>
      <Modal.Header className="px-card border-0">
        <div className="w-100 d-flex justify-content-between align-items-center">
          <Modal.Title className="mb-0">
            {readOnly ? 'Visualizar observação' : 'Editar Intervenção'}
          </Modal.Title>
          <Button className="p-1 fs-10 text-body" onClick={handleHide}>
            X
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body className="p-card py-0">
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Intervenção</Form.Label>
            <Form.Select
              value={stateId}
              onChange={e => setStateId(Number(e.target.value))}
              disabled={readOnly}
            >
              {stateId === 2 && <option value={2}>Observação</option>}
              <option value={3}>Avaria</option>
              <option value={4}>Manutenção</option>
              <option value={5}>Inspeção</option>
              <option value={6}>Acidente</option>
            </Form.Select>
          </Form.Group>

          {readOnly ? (
            // Read-only: Single combined datetime input
            <Form.Group className="mb-3">
              <Form.Label>Data de Início</Form.Label>
              <Form.Control
                type="text"
                value={
                  startDay && startTime
                    ? dayjs(startDay).format('YYYY-MM-DD HH:mm')
                    : ''
                }
                readOnly
              />
            </Form.Group>
          ) : (
            // Edit mode: Original date/time controls
            <>
              {/* Start Date and Time Row */}
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      {readOnly ? 'Data de acontecimento' : 'Data de Início'}
                    </Form.Label>
                    <DatePicker
                      value={startDay}
                      options={{
                        dateFormat: 'Y-m-d',
                        enableTime: false
                      }}
                      onChange={([date]) => date && setStartDay(date)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Hora de Início</Form.Label>
                    <Form.Control
                      type="time"
                      value={startTime}
                      onChange={e => setStartTime(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* End Date and Time Row - with slide animation */}
              <div className={`end-date-container ${hasEndDate ? 'show' : ''}`}>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Data de Fim</Form.Label>
                      <DatePicker
                        value={endDay}
                        options={{
                          dateFormat: 'Y-m-d',
                          enableTime: false
                        }}
                        onChange={([date]) => date && setEndDay(date)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Hora de Fim</Form.Label>
                      <Form.Control
                        type="time"
                        value={endTime}
                        onChange={e => setEndTime(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Toggle for end date */}
              <div
                className="d-flex align-items-center my-3 toggle-end-date"
                onClick={() => setHasEndDate(!hasEndDate)}
                style={{ cursor: 'pointer' }}
              >
                <div
                  className="flex-grow-1"
                  style={{ borderTop: '1px solid #dee2e6' }}
                ></div>
                <span
                  className="mx-2"
                  style={{ color: '#6c757d', fontSize: '0.875rem' }}
                >
                  {hasEndDate ? '▲' : '▼'}
                </span>
                <div
                  className="flex-grow-1"
                  style={{ borderTop: '1px solid #dee2e6' }}
                ></div>
              </div>
            </>
          )}

          <Form.Group className="mb-3">
            <Form.Label>
              {readOnly ? 'Observações' : 'Observações (Opcional)'}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={observation}
              onChange={e => setObservation(e.target.value)}
              readOnly={readOnly}
            />
          </Form.Group>

          {readOnly && (
            <Form.Group className="mb-3">
              <Form.Label>Local</Form.Label>
              <Form.Control type="text" value={local} readOnly />
            </Form.Group>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-end border-0">
        {!readOnly && (
          <Button variant="primary" onClick={handleUpdate}>
            Guardar
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default EditMaintenanceModal;
